// Third-party
import React, { useState, useCallback } from 'react';
import get from 'lodash/get';
import DOMPurify from 'dompurify';

// Types
import Comparison from '@/types/comparison';
import ComparisonItem from '@/types/comparison-item';
import ComparisonCategory from '@/types/comparison-category';
import ComparisonField from '@/types/comparison-field';
import Locale from '@/types/locale';
import RiskPeriods from '@/types/risk-periods';

// Utils
import { formatMessage, formatLocalizedString } from '@/utils/i18n';
import { RISK_PERIODS, COMPARISON_CATEGORIES } from '@/utils/constants';
import { getCustomDisclaimersForItem } from '@/utils/disclaimers/comparison';
import { DISCLAIMER_TAG } from '@/utils/disclaimers/constants';

// Components
import { Button } from '@/views/components/buttons';
import { GenericTextModal } from '@/views/components/modals';
import AccessibleTable from './AccessibleTable';

interface Props {
  comparison: Comparison;
  items: ComparisonItem[];
  categories: ComparisonCategory[];
  locale: Locale;
  onModifyPortfolio: (item: ComparisonItem) => void;
  onViewPortfolio: (item: ComparisonItem) => void;
  onClonePortfolio: (item: ComparisonItem) => void;
  onModifyBenchmark: (item: ComparisonItem) => void;
  onCloneBenchmark: (item: ComparisonItem) => void;
  onItemRemove: (item: ComparisonItem) => void;
  onSavePortfolio: (item: ComparisonItem, toContact?: boolean) => void;
  addCategoryBenchmark: (item: ComparisonItem) => void;
  readOnly?: boolean;
}

interface FlattenedRow {
  itemId: string;
  itemName: string;
  itemType: string;
  categoryValues: { [categoryKey: string]: { [fieldKey: string]: any } };
  disclaimers?: { [categoryKey: string]: { [fieldKey: string]: string[] } };
}

/**
 * Accessible Comparison Table Component
 * Provides a screen reader-friendly view of comparison data
 */
const AccessibleComparisonTable: React.FC<Props> = ({
  comparison,
  items,
  categories,
  locale,
  onModifyPortfolio,
  onViewPortfolio,
  onClonePortfolio,
  onModifyBenchmark,
  onCloneBenchmark,
  onItemRemove,
  onSavePortfolio,
  readOnly,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showCustomDisclaimerModal, setShowCustomDisclaimerModal] = useState(false);
  const [selectedDisclaimerItem, setSelectedDisclaimerItem] = useState<ComparisonItem | null>(null);

  const onViewFullDisclaimer = useCallback((item: ComparisonItem) => {
    setSelectedDisclaimerItem(item);
    setShowCustomDisclaimerModal(true);
  }, []);

  const hideCustomDisclaimerModal = useCallback(() => {
    setShowCustomDisclaimerModal(false);
    setSelectedDisclaimerItem(null);
  }, []);

  const getCustomDisclaimerModalContent = useCallback(() => {
    if (!selectedDisclaimerItem) return <div>{formatMessage('AccessibleTable.NoData')}</div>;

    const customDisclaimersForItem = getCustomDisclaimersForItem(selectedDisclaimerItem, [DISCLAIMER_TAG.COMPARISON]);
    const disclaimers = Array.from(new Set(customDisclaimersForItem.map(d => JSON.stringify(d)))).map(str => JSON.parse(str));

    if (disclaimers.length === 0) {
      return <div>{formatMessage('AccessibleTable.NoData')}</div>;
    }

    return (
      <>
        {disclaimers.map(d => (
          <React.Fragment key={d.symbol}>
            <h3>{d.name}</h3>
            <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatLocalizedString(d.content)) }} />
          </React.Fragment>
        ))}
      </>
    );
  }, [selectedDisclaimerItem]);

  /**
   * Gets the value for a specific field from an item using the same logic as ComparisonValueCell
   */
  const getCellValue = useCallback((comparisonItem: ComparisonItem, field: ComparisonField, riskPeriod?: RiskPeriods): any => {
    if (field.accessor) {
      if (field.multiValue && riskPeriod) {
        return field.accessor(comparisonItem, riskPeriod);
      }
      return field.accessor(comparisonItem);
    }

    // Keys use '_' as separators and we need to replace them with '.' when accessing a field
    const formattedKey = field.key.replace(/_/g, '.');

    return riskPeriod ? get(comparisonItem, `${formattedKey}.${riskPeriod}`, null) : get(comparisonItem, formattedKey, null);
  }, []);

  /**
   * Gets disclaimer for a field if it exists
   */
  const getFieldDisclaimer = useCallback((field: ComparisonField, item: ComparisonItem, fieldKey: string): string[] => {
    if (!field.getValueDisclaimer || typeof field.getValueDisclaimer !== 'function') {
      return [];
    }

    try {
      const disclaimer = field.getValueDisclaimer(item);
      if (disclaimer && disclaimer.message) {
        return [disclaimer.message];
      }
    } catch (error) {
      console.warn(`Error getting disclaimer for field ${fieldKey} for item ${item._id}:`, error);
    }

    return [];
  }, []);

  /**
   * Flattens comparison data for accessible table display
   * Organizes data by items (rows) with categories as columns
   */
  const flattenComparisonData = useCallback((): FlattenedRow[] => {
    if (!categories || !items.length) {
      return [];
    }

    const flattenedRows: FlattenedRow[] = [];

    // Define risk periods to show for multiValue fields
    const riskPeriods = [RISK_PERIODS.YT, RISK_PERIODS['1Y'], RISK_PERIODS['3Y'], RISK_PERIODS['5Y'], RISK_PERIODS['10Y'], RISK_PERIODS.INC, RISK_PERIODS.CI];

    // Create one row per comparison item
    items.forEach(item => {
      const categoryValues: { [categoryKey: string]: { [fieldKey: string]: any } } = {};
      const disclaimers: { [categoryKey: string]: { [fieldKey: string]: string[] } } = {};

      // For each category, collect all field values for this item
      categories.forEach(category => {
        categoryValues[category.key] = {};
        disclaimers[category.key] = {};

        category.fields.forEach((field: ComparisonField) => {
          // Calendar year returns fields are already individual year fields and should not be expanded by risk periods
          const isCalendarYearField = category.key === COMPARISON_CATEGORIES.CALENDAR_YEAR_RETURNS;

          if (field.multiValue && !isCalendarYearField) {
            // Some multiValue fields should not be expanded by risk period
            // - Portfolio correlation shows a matrix, not individual risk metric values
            const shouldExpandByRiskPeriod = field.key !== 'performance_correlation';

            if (shouldExpandByRiskPeriod) {
              // For multiValue fields (like risk metrics), create separate entries for each risk period
              riskPeriods.forEach(period => {
                const value = getCellValue(item, field, period);
                const fieldKeyWithPeriod = `${field.key}_${period}`;

                categoryValues[category.key][fieldKeyWithPeriod] = value;
                disclaimers[category.key][fieldKeyWithPeriod] = getFieldDisclaimer(field, item, fieldKeyWithPeriod);
              });
            } else {
              // For correlation and other special multiValue fields, use the comparison's risk period
              const value = getCellValue(item, field, comparison.riskPeriod);
              categoryValues[category.key][field.key] = value;
              disclaimers[category.key][field.key] = getFieldDisclaimer(field, item, field.key);
            }
          } else {
            // For non-multiValue fields (including all calendar year fields), get value directly
            const value = getCellValue(item, field);
            categoryValues[category.key][field.key] = value;
            disclaimers[category.key][field.key] = getFieldDisclaimer(field, item, field.key);
          }
        });
      });

      flattenedRows.push({
        itemId: item._id,
        itemName: formatLocalizedString(item.name, locale),
        itemType: item.type,
        categoryValues,
        disclaimers,
      });
    });

    return flattenedRows;
  }, [categories, items, getCellValue, getFieldDisclaimer, locale, comparison.riskPeriod]);

  const handleToggleVisibility = useCallback(() => {
    setIsVisible(!isVisible);
  }, [isVisible]);

  const flattenedData = flattenComparisonData();

  return (
    <div className="accessible-comparison-table-wrapper">
      <div className="accessible-comparison-table-toggle">
        <Button
          compact
          noBackground
          onClick={handleToggleVisibility}
          className="accessible-table-toggle-button"
          aria-describedby="accessible-table-description"
          aria-expanded={isVisible}
        >
          {isVisible ? formatMessage('AccessibleTable.Hide') : formatMessage('AccessibleTable.OpenAccessibleTableView')}
        </Button>
        <div id="accessible-table-description" className="visually-hidden">
          {formatMessage('AccessibleTable.Description')}
        </div>
      </div>

      {isVisible && (
        <div className="accessible-table-content">
          <div className="accessible-table-region" role="region" aria-label={formatMessage('AccessibleTable.RegionLabel')}>
            <AccessibleTable
              items={items}
              flattenedData={flattenedData}
              comparison={comparison}
              categories={categories}
              locale={locale}
              riskPeriod={comparison.riskPeriod}
              onModifyPortfolio={onModifyPortfolio}
              onViewPortfolio={onViewPortfolio}
              onClonePortfolio={onClonePortfolio}
              onModifyBenchmark={onModifyBenchmark}
              onCloneBenchmark={onCloneBenchmark}
              onItemRemove={onItemRemove}
              onSavePortfolio={onSavePortfolio}
              onViewFullDisclaimer={onViewFullDisclaimer}
              readOnly={readOnly}
            />
          </div>
        </div>
      )}

      <GenericTextModal
        backdropClassName="modal-v2-backdrop"
        modalClassName="modal-v2"
        title={formatMessage('Common.Disclaimer')}
        show={showCustomDisclaimerModal}
        onCancel={hideCustomDisclaimerModal}
        body={getCustomDisclaimerModalContent()}
        isV2
      />
    </div>
  );
};

export default AccessibleComparisonTable;
